import { useEffect, useMemo, useState } from 'react'

import { useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  PLATFORM_WINDOWS,
  TestingCheckScreen,
  TestingHomeScreen,
  TestingRunScreen,
  TestingRunsScreen,
  TestProductScreen,
  TestProductsScreen,
  buildTestingCheckVm,
  buildTestingRunsVm,
  buildTestingRunVm,
  buildTestProductsVm,
  buildTestProductVm,
  holdFrame,
  lastTestingCommand,
  parseBarcodes,
  platformCatalogProducts,
  platformTestingAddPhoto,
  platformTestingCancelRun,
  platformTestingCheckStored,
  platformTestingCopyFromCatalog,
  platformTestingCreate,
  platformTestingDelete,
  platformTestingDeletePhoto,
  platformTestingProduct,
  platformTestingProducts,
  platformTestingRefresh,
  platformTestingReindex,
  platformTestingRun,
  platformTestingRuns,
  platformTestingStartRun,
  platformTestingUpdate,
  platformTextRuns,
  releaseFrame,
  testingAccessOf,
  testingPhotoUrl,
  type TestingAccess,
  type TestingCheckActions,
  type TestingHomeActions,
  type TestingKind,
  type TestingRunActions,
  type TestingRunsActions,
  type TestProductActions,
  type TestProductDraft,
  type TestProductsActions,
} from '@krp/platform-ui'

import { useAppSelector } from '../../app/hooks'
import { RUN_POLL_MS } from './timings'
import { useMyAccess } from './useMyAccess'

const CATALOG_PAGE = 10

function useTestingAccess(): TestingAccess {
  return testingAccessOf(useMyAccess())
}

const fieldsOf = (draft: TestProductDraft) => ({
  name: draft.name.trim(),
  sku: draft.sku.trim() === '' ? null : draft.sku.trim(),
  barcodes: parseBarcodes(draft.barcodes),
})

/** «Тестирование»: виды проверок. */
export function TestingHomeBinding() {
  const access = useTestingAccess()
  const openWindow = useOpenWindow()
  const actions = useMemo<TestingHomeActions>(
    () => ({
      openPhotoSearch: () => openWindow(PLATFORM_WINDOWS.testingProducts),
      openTextSearch: () => openWindow(PLATFORM_WINDOWS.testingTextQueries),
      openTwin: () => openWindow(PLATFORM_WINDOWS.testingTwin),
    }),
    [openWindow],
  )
  return <TestingHomeScreen access={access} actions={actions} />
}

const selectProducts = platformTestingProducts.select()

/**
 * Тестовые товары общие у поиска по фото и по тексту; у каждого свой экран (RP-FB34):
 * из поиска по тексту карточка товара — без фото, а «Замеры» ведут в его замеры.
 */
const KIND_WINDOWS = {
  photo: { product: PLATFORM_WINDOWS.testingProduct, runs: PLATFORM_WINDOWS.testingRuns },
  text: { product: PLATFORM_WINDOWS.testingTextProduct, runs: PLATFORM_WINDOWS.testingTextRuns },
} as const satisfies Record<TestingKind, { product: string; runs: string }>

/** «Поиск по фото · Товары»: список, новый товар и копия товара каталога. */
export function TestProductsBinding() {
  return <ProductsBinding kind="photo" />
}

/** «Поиск по тексту · Товары»: те же тестовые товары, без фото. */
export function TextProductsBinding() {
  return <ProductsBinding kind="text" />
}

function ProductsBinding({ kind }: { kind: TestingKind }) {
  const access = useTestingAccess()
  const openWindow = useOpenWindow()
  const skip = access !== 'allowed'
  const productsSubscription = platformTestingProducts.useQuerySubscription(undefined, { skip })
  const products = useAppSelector(selectProducts)

  // Строка поиска по каталогу — намерение этого окна, живёт пока окно открыто.
  const [catalogQuery, setCatalogQuery] = useState<string | null>(null)
  const catalogArgs = useMemo(() => ({ query: catalogQuery ?? '', page: 1, pageSize: CATALOG_PAGE }), [catalogQuery])
  platformCatalogProducts.useQuerySubscription(catalogArgs, { skip: skip || catalogQuery === null })
  const catalog = useAppSelector(useMemo(() => platformCatalogProducts.select(catalogArgs), [catalogArgs]))

  const [create, createResult] = platformTestingCreate.useMutation()
  const [copy, copyResult] = platformTestingCopyFromCatalog.useMutation()
  const [remove, removeResult] = platformTestingDelete.useMutation()

  const vm = buildTestProductsVm({
    products,
    catalog: catalogQuery === null ? null : catalog,
    command: lastTestingCommand([createResult, copyResult, removeResult]),
  })

  const refetch = productsSubscription.refetch
  const actions = useMemo<TestProductsActions>(
    () => ({
      reload: () => void refetch(),
      create: (draft) => {
        void create(fieldsOf(draft))
          .unwrap()
          .then((created) => openWindow(KIND_WINDOWS[kind].product, { productId: created.product.id }))
          .catch(() => undefined)
      },
      searchCatalog: setCatalogQuery,
      copyFromCatalog: (catalogProductId) => {
        void copy({ catalogProductId })
          .unwrap()
          .then((created) => openWindow(KIND_WINDOWS[kind].product, { productId: created.product.id }))
          .catch(() => undefined)
      },
      open: (productId) => openWindow(KIND_WINDOWS[kind].product, { productId }),
      remove: (productId) => void remove({ productId }),
      openRuns: () => openWindow(KIND_WINDOWS[kind].runs),
    }),
    [copy, create, kind, openWindow, refetch, remove],
  )

  return <TestProductsScreen access={access} vm={vm} actions={actions} kind={kind} />
}

/** Карточка тестового товара. Правка несёт версию, которую человек видит: устаревшая — 409, а не перезапись. */
export function TestProductBinding() {
  return <ProductBinding kind="photo" />
}

/** Карточка тестового товара из поиска по тексту: только поля. */
export function TextProductBinding() {
  return <ProductBinding kind="text" />
}

function ProductBinding({ kind }: { kind: TestingKind }) {
  const access = useTestingAccess()
  const window = useWindow()
  const openWindow = useOpenWindow()
  const productId = window.params.productId ?? ''
  const args = useMemo(() => ({ productId }), [productId])
  const subscription = platformTestingProduct.useQuerySubscription(args, { skip: access !== 'allowed' || productId === '' })
  const product = useAppSelector(useMemo(() => platformTestingProduct.select(args), [args]))

  const [update, updateResult] = platformTestingUpdate.useMutation()
  const [refresh, refreshResult] = platformTestingRefresh.useMutation()
  const [reindex, reindexResult] = platformTestingReindex.useMutation()
  const [addPhoto, addResult] = platformTestingAddPhoto.useMutation()
  const [removePhoto, removePhotoResult] = platformTestingDeletePhoto.useMutation()
  const [remove, removeResult] = platformTestingDelete.useMutation()
  const [removed, setRemoved] = useState(false)

  const vm = buildTestProductVm({
    product,
    command: lastTestingCommand([updateResult, refreshResult, reindexResult, addResult, removePhotoResult, removeResult]),
  })
  const version = product.data?.product.version ?? 0
  const refetch = subscription.refetch

  const actions = useMemo<TestProductActions>(
    () => ({
      reload: () => void refetch(),
      save: (draft) => void update({ productId, expectedVersion: version, ...fieldsOf(draft) }),
      refreshFromSource: () => void refresh({ productId }),
      reindex: () => void reindex({ productId }),
      addPhoto: (role, file, tags) => {
        const frameId = holdFrame(file)
        void addPhoto({ productId, frameId, role, tags })
          .unwrap()
          .catch(() => undefined)
          .finally(() => releaseFrame(frameId))
      },
      removePhoto: (photoId) => void removePhoto({ productId, photoId }),
      checkFrame: (photoId) => openWindow(PLATFORM_WINDOWS.testingCheck, { photoId }),
      remove: () => {
        void remove({ productId })
          .unwrap()
          .then(() => setRemoved(true))
          .catch(() => undefined)
      },
    }),
    [addPhoto, openWindow, productId, refetch, refresh, reindex, remove, removePhoto, update, version],
  )

  if (removed) return <p role="status">Тестовый товар удалён вместе с фото и точками тестового индекса.</p>
  return <TestProductScreen access={access} vm={vm} actions={actions} kind={kind} />
}

/** Проверка кадра: запускается при открытии окна и по «Проверить снова». */
export function TestingCheckBinding() {
  const access = useTestingAccess()
  const window = useWindow()
  const photoId = window.params.photoId ?? ''
  const [check, checkResult] = platformTestingCheckStored.useMutation()

  useEffect(() => {
    if (access === 'allowed' && photoId !== '') void check({ photoId, lanes: 'all' })
  }, [access, check, photoId])

  const vm = buildTestingCheckVm(checkResult)
  const actions = useMemo<TestingCheckActions>(() => ({ recheck: () => void check({ photoId, lanes: 'all' }) }), [check, photoId])
  return <TestingCheckScreen access={access} frameUrl={testingPhotoUrl(photoId)} vm={vm} actions={actions} />
}

const selectRuns = platformTestingRuns.select()
const selectTextRuns = platformTextRuns.select()

/** Прогоны: последний балл, история, запуск и отмена. */
export function TestingRunsBinding() {
  const access = useTestingAccess()
  const openWindow = useOpenWindow()
  const runs = useAppSelector(selectRuns)
  const active = (runs.data ?? []).some((run) => run.state === 'queued' || run.state === 'running')
  const subscription = platformTestingRuns.useQuerySubscription(undefined, { skip: access !== 'allowed', pollingInterval: active ? RUN_POLL_MS : 0 })
  // Очередь замеров общая с поиском по тексту: идущий замер по тексту тоже не даёт запустить свой — и опрашивается, пока не кончится.
  const textRuns = useAppSelector(selectTextRuns)
  const textActive = (textRuns.data ?? []).some((run) => run.state === 'queued' || run.state === 'running')
  platformTextRuns.useQuerySubscription(undefined, { skip: access !== 'allowed', pollingInterval: textActive ? RUN_POLL_MS : 0 })
  const [start, startResult] = platformTestingStartRun.useMutation()
  const [cancel, cancelResult] = platformTestingCancelRun.useMutation()

  const vm = buildTestingRunsVm({ runs, otherActive: textActive, command: lastTestingCommand([startResult, cancelResult]) })
  const refetch = subscription.refetch
  const actions = useMemo<TestingRunsActions>(
    () => ({
      reload: () => void refetch(),
      start: (lanes) => void start({ lanes }),
      cancel: (runId) => void cancel({ runId }),
      open: (runId) => openWindow(PLATFORM_WINDOWS.testingRun, { runId }),
      openProducts: () => openWindow(PLATFORM_WINDOWS.testingProducts),
    }),
    [cancel, openWindow, refetch, start],
  )
  return <TestingRunsScreen access={access} vm={vm} actions={actions} />
}

/** Разбор прогона; пока он идёт — опрашивается. */
export function TestingRunBinding() {
  const access = useTestingAccess()
  const window = useWindow()
  const openWindow = useOpenWindow()
  const runId = window.params.runId ?? ''
  const args = useMemo(() => ({ runId }), [runId])
  const run = useAppSelector(useMemo(() => platformTestingRun.select(args), [args]))
  const state = run.data?.run.state
  const active = state === 'queued' || state === 'running'
  const subscription = platformTestingRun.useQuerySubscription(args, {
    skip: access !== 'allowed' || runId === '',
    pollingInterval: active ? RUN_POLL_MS : 0,
  })
  const [cancel, cancelResult] = platformTestingCancelRun.useMutation()

  const vm = buildTestingRunVm({ run, command: lastTestingCommand([cancelResult]) })
  const refetch = subscription.refetch
  const actions = useMemo<TestingRunActions>(
    () => ({
      reload: () => void refetch(),
      cancel: () => void cancel({ runId }),
      openProduct: (productId) => openWindow(PLATFORM_WINDOWS.testingProduct, { productId }),
      openRun: (previousRunId) => openWindow(PLATFORM_WINDOWS.testingRun, { runId: previousRunId }),
    }),
    [cancel, openWindow, refetch, runId],
  )
  return <TestingRunScreen access={access} vm={vm} actions={actions} />
}
